import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";
import SellerDashboard from "@/pages/SellerDashboard";
import HRDashboard from "@/pages/HRDashboard";
import ConcessionsPage from "@/pages/ConcessionsPage";
import SellersPage from "@/pages/SellersPage";
import ObjectivesPage from "@/pages/ObjectivesPage";
import ObjectiveFormPage from "@/pages/ObjectiveFormPage";
import SignaturePage from "@/pages/SignaturePage";
import HistoryPage from "@/pages/HistoryPage";
import SettingsPage from "@/pages/SettingsPage";
import SeasonalityPage from "@/pages/SeasonalityPage";
import PerformancePage from "@/pages/PerformancePage";
import ExportPage from "@/pages/ExportPage";
import ManagersPage from "@/pages/ManagersPage";
import AdminToolsPage from "@/pages/AdminToolsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// ===================== STORAGE HELPER (Multi-browser compatible) =====================
// Gère localStorage avec fallback sur sessionStorage et mémoire
const StorageHelper = {
  _memoryStorage: {},
  _storageAvailable: null,

  // Vérifie si localStorage est disponible (Safari mode privé, etc.)
  isLocalStorageAvailable() {
    if (this._storageAvailable !== null) return this._storageAvailable;
    try {
      const testKey = '__storage_test__';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      this._storageAvailable = true;
    } catch (e) {
      this._storageAvailable = false;
      console.warn('localStorage non disponible, utilisation du fallback mémoire');
    }
    return this._storageAvailable;
  },

  getItem(key) {
    try {
      if (this.isLocalStorageAvailable()) {
        return window.localStorage.getItem(key);
      }
      // Fallback sessionStorage
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        // Fallback mémoire
        return this._memoryStorage[key] || null;
      }
    } catch (e) {
      console.warn('Erreur lecture storage:', e);
      return this._memoryStorage[key] || null;
    }
  },

  setItem(key, value) {
    try {
      if (this.isLocalStorageAvailable()) {
        window.localStorage.setItem(key, value);
        return;
      }
      // Fallback sessionStorage
      try {
        window.sessionStorage.setItem(key, value);
        return;
      } catch (e) {
        // Fallback mémoire
        this._memoryStorage[key] = value;
      }
    } catch (e) {
      console.warn('Erreur écriture storage:', e);
      this._memoryStorage[key] = value;
    }
  },

  removeItem(key) {
    try {
      if (this.isLocalStorageAvailable()) {
        window.localStorage.removeItem(key);
      }
      try {
        window.sessionStorage.removeItem(key);
      } catch (e) {}
      delete this._memoryStorage[key];
    } catch (e) {
      console.warn('Erreur suppression storage:', e);
      delete this._memoryStorage[key];
    }
  },

  // Parse JSON de manière sécurisée
  getJSON(key) {
    try {
      const item = this.getItem(key);
      if (!item) return null;
      return JSON.parse(item);
    } catch (e) {
      console.warn('Erreur parsing JSON pour', key, ':', e);
      this.removeItem(key); // Supprimer les données corrompues
      return null;
    }
  },

  setJSON(key, value) {
    try {
      this.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn('Erreur stringify JSON pour', key, ':', e);
    }
  }
};

export const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 secondes par défaut
  withCredentials: false, // Évite les problèmes CORS avec credentials
});

// Endpoints admin lourds (sync Yousign, backup complet) : timeout étendu à 3 min
api.interceptors.request.use((config) => {
  const url = config.url || "";
  if (
    url.includes("/admin/sync-yousign") ||
    url.includes("/admin/force-fix-yousign") ||
    url.includes("/admin/force-sync") ||
    url.includes("/admin/backup") ||
    url.includes("/admin/diagnose-yousign")
  ) {
    config.timeout = 180000; // 3 minutes
  }
  return config;
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = StorageHelper.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle auth errors with better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Gestion des erreurs réseau (timeout, pas de connexion)
    if (!error.response) {
      console.error('Erreur réseau:', error.message);
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401) {
      StorageHelper.removeItem("token");
      StorageHelper.removeItem("user");
      // Utiliser replace pour éviter les problèmes de navigation
      if (window.location.pathname !== '/login') {
        window.location.replace("/login");
      }
    }
    return Promise.reject(error);
  }
);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = StorageHelper.getItem("token");
        const savedUser = StorageHelper.getJSON("user");
        
        if (token && savedUser) {
          setUser(savedUser);
          // Verify token is still valid
          try {
            const res = await api.get("/auth/me");
            setUser(res.data);
            StorageHelper.setJSON("user", res.data);
          } catch (err) {
            console.warn('Token invalide, déconnexion');
            StorageHelper.removeItem("token");
            StorageHelper.removeItem("user");
            setUser(null);
          }
        }
      } catch (err) {
        console.error('Erreur initialisation auth:', err);
        StorageHelper.removeItem("token");
        StorageHelper.removeItem("user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      const { token, user: userData } = res.data;
      
      // Stocker de manière sécurisée
      StorageHelper.setItem("token", token);
      StorageHelper.setJSON("user", userData);
      setUser(userData);
      
      return userData;
    } catch (error) {
      // Nettoyer en cas d'erreur
      StorageHelper.removeItem("token");
      StorageHelper.removeItem("user");
      throw error;
    }
  };

  const logout = () => {
    StorageHelper.removeItem("token");
    StorageHelper.removeItem("user");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    const dashboardRoutes = {
      admin: "/admin",
      manager: "/manager",
      seller: "/seller",
      hr: "/hr",
    };
    return <Navigate to={dashboardRoutes[user.role] || "/login"} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr"]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/concessions"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr"]}>
            <ConcessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seasonality"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr"]}>
            <SeasonalityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr"]}>
            <ExportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/managers"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr"]}>
            <ManagersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-tools"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr"]}>
            <AdminToolsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Manager Routes */}
      <Route
        path="/manager"
        element={
          <ProtectedRoute allowedRoles={["manager"]}>
            <ManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sellers"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr", "manager"]}>
            <SellersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/objectives"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr", "manager"]}>
            <ObjectivesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/objectives/new"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr", "manager"]}>
            <ObjectiveFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/objectives/:id/edit"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr", "manager"]}>
            <ObjectiveFormPage />
          </ProtectedRoute>
        }
      />
      
      {/* Seller Routes */}
      <Route
        path="/seller"
        element={
          <ProtectedRoute allowedRoles={["seller"]}>
            <SellerDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* HR Routes */}
      <Route
        path="/hr"
        element={
          <ProtectedRoute allowedRoles={["hr"]}>
            <HRDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Signature Route (all authenticated users) */}
      <Route
        path="/sign/:id"
        element={
          <ProtectedRoute allowedRoles={["admin", "hr", "manager", "seller"]}>
            <SignaturePage />
          </ProtectedRoute>
        }
      />
      
      {/* History Route */}
      <Route
        path="/history"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "hr"]}>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      
      {/* Performance Route */}
      <Route
        path="/performance"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "hr"]}>
            <PerformancePage />
          </ProtectedRoute>
        }
      />
      
      {/* Settings Route (all authenticated users) */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={["admin", "manager", "seller", "hr"]}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Default redirect */}
      <Route
        path="/"
        element={
          user ? (
            <Navigate to={`/${user.role === "admin" ? "admin" : user.role}`} replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      
      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
